import { useEffect, useRef, useState } from 'react'

type UseWaterfallIntroOptions = {
  key: string
  count: number
  enabled?: boolean
  stepMs?: number
  onStep?: (index: number) => void
  onComplete?: () => void
}

export default function useWaterfallIntro({
  key,
  count,
  enabled = true,
  stepMs = 110,
  onStep,
  onComplete,
}: UseWaterfallIntroOptions) {
  const [visibleCount, setVisibleCount] = useState(enabled ? 0 : count)
  const [isAnimating, setIsAnimating] = useState(false)
  const lastKeyRef = useRef<string | null>(null)
  const timersRef = useRef<number[]>([])
  const stepRef = useRef(onStep)
  const completeRef = useRef(onComplete)

  useEffect(() => {
    stepRef.current = onStep
  }, [onStep])

  useEffect(() => {
    completeRef.current = onComplete
  }, [onComplete])

  const clearTimers = () => {
    timersRef.current.forEach((timer) => window.clearTimeout(timer))
    timersRef.current = []
  }

  useEffect(() => {
    return () => clearTimers()
  }, [])

  useEffect(() => {
    if (!enabled) {
      clearTimers()
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setVisibleCount(count)
      setIsAnimating(false)
      return
    }
    if (!count) {
      clearTimers()
      setVisibleCount(0)
      setIsAnimating(false)
      return
    }
    if (lastKeyRef.current === key) return

    lastKeyRef.current = key
    clearTimers()
    setIsAnimating(true)
    setVisibleCount(0)

    for (let i = 1; i <= count; i += 1) {
      timersRef.current.push(
        window.setTimeout(() => {
          setVisibleCount(i)
          stepRef.current?.(i)
          if (i === count) {
            setIsAnimating(false)
            completeRef.current?.()
          }
        }, stepMs * i)
      )
    }
  }, [key, count, enabled, stepMs])

  return { visibleCount, isAnimating }
}
